import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AnalyticsPeriod, EventTrackingLogRow } from '../../models/analytics.types';
import {
  EventTrackingAnalyticsResponse,
  EventTrackingSummary,
  EventTrackingTangramOption,
  ProfessorAnalyticsService,
} from '../../services/professor-analytics.service';

// Sentinel for the "All Tangrams" option in the dropdown — matches the
// convention used by Content Metrics / Educator View tabs.
const ALL_TANGRAMS = '';

@Component({
  selector: 'tangram-pa-event-tracking-tab',
  templateUrl: './event-tracking-tab.component.html',
  styleUrls: ['./event-tracking-tab.component.scss']
})
export class EventTrackingTabComponent implements OnInit, OnChanges {
  // Period flows down from the parent — single source of truth for the
  // dashboard-wide period dropdown.
  @Input() period: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  loading = true;
  error = '';

  // Tangram filter — owned by this tab, scopes every metric server-side.
  selectedPathwayId: string = ALL_TANGRAMS;

  tangrams: EventTrackingTangramOption[] = [];
  rows: EventTrackingLogRow[] = [];
  summary: EventTrackingSummary = {
    totalEvents: 0,
    totalUniqueUsers: 0,
    eventsTracked: 0,
  };

  // Cancel in-flight request before starting a new one — protects against
  // a fast period / Tangram switch overwriting fresh data with stale.
  private fetchSub?: Subscription;

  constructor(private analyticsService: ProfessorAnalyticsService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['period'] || changes['startDate'] || changes['endDate']) &&
        !(changes['period'] && changes['period'].firstChange)) {
      this.load();
    }
  }

  onPathwayChange(pathwayId: string): void {
    this.selectedPathwayId = pathwayId;
    this.load();
  }

  get selectedTangramTitle(): string {
    if (!this.selectedPathwayId) { return 'All Tangrams'; }
    const match = this.tangrams.find((t) => t.pathwayId === this.selectedPathwayId);
    return match ? match.title : 'Selected Tangram';
  }

  private load(): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    this.loading = true;
    this.error = '';

    this.fetchSub = this.analyticsService
      .getEventTrackingAnalytics(this.period, this.selectedPathwayId, this.startDate, this.endDate)
      .subscribe(
        (data: EventTrackingAnalyticsResponse) => {
          this.tangrams = data.tangrams || [];
          this.rows = data.eventTrackingLog || [];
          this.summary = data.summary || this.summary;
          // If the server fell back to "all" because the supplied id wasn't
          // owned by the professor, keep the dropdown honest.
          if (data.filter && typeof data.filter.pathwayId === 'string') {
            this.selectedPathwayId = data.filter.pathwayId;
          }
          // Publish to the per-tab export cache (see service comment).
          this.analyticsService.setLatestTabData('event-tracking', data);
          this.loading = false;
        },
        (err) => {
          this.error = (err && err.message) ? err.message : 'Failed to load Event Tracking metrics.';
          this.loading = false;
        },
      );
  }
}
